/**
 * ── The SERIALISED impact report document ──────────────────────────────────────────────────────
 *
 * Fixed strings and numbers for the standalone HTML document handed to the external PDF
 * converter. Nothing here describes the on-screen report: the screen has an app shell, a Tailwind
 * build and a live origin behind it, and the serialised document has none of the three.
 *
 * Kept out of `constants/impactReport.ts` on purpose. That file is the report FEATURE's vocabulary
 * — labels, routes, error messages — read by a dozen components. This file describes a printed
 * artefact, and folding it in would leave the next reader unable to tell which constants a
 * component may reach for.
 */

/**
 * The printed page box, in CSS pixels, and the ONLY page geometry either half of this feature
 * states. The converter deliberately imposes and overrides none — page geometry is this side's to
 * own — so if this is absent the document prints at the converter's default paper and every
 * `clamp(…, Nvw, …)` type size in the document stylesheet resolves against the wrong viewport.
 *
 * 1440 x 900 is the desktop viewport the document's type scale was designed and reviewed at, not
 * a paper size: `.irv-title`, `.irv-stat-value` and `.irv-sum-value` are all viewport-relative, so
 * printing at any other width silently re-sizes the report's display type. The height is also
 * every printed sheet's explicit `height` in `IMPACT_REPORT_DOCUMENT_GEOMETRY_CSS`, which is what
 * makes one page model entry fill exactly one sheet.
 *
 * CSS fixes 96 px to the inch regardless of device, so this is 15in x 9.375in of paper.
 */
export const IMPACT_REPORT_DOCUMENT_PAGE_WIDTH_PX = 1440;
export const IMPACT_REPORT_DOCUMENT_PAGE_HEIGHT_PX = 900;

/**
 * The width cause photography is REQUESTED at, as the CDN's own resize parameter.
 *
 * THE ARITHMETIC, so the next reader can check it rather than trust it:
 *   - Placed size: `.irv-page-bg` is `position: absolute; inset: 0; object-fit: cover`, so a
 *     backdrop is placed at exactly one full page — 1440 x 900 CSS px above, 15in x 9.375in.
 *   - 2400 px / 15in = 160 DPI across, and the CDN derives the height from the aspect ratio, so a
 *     backdrop that fills the page arrives at 1500 px / 9.375in = 160 DPI down as well.
 *   - 160 DPI sits mid-band of the 150–200 DPI a photograph needs to read as a photograph in
 *     print, and well under the 300 DPI that would triple the bytes for no visible gain.
 *
 * This is a REQUEST, never a crop or a scale: the parameter rides on the URL and the CDN answers
 * with a rendition. A host that does not recognise it serves the original, which is a bigger file
 * than necessary — never a broken image.
 */
export const IMPACT_REPORT_DOCUMENT_BACKDROP_WIDTH_PX = 2400;

/** The query parameter carrying the width above. */
export const IMPACT_REPORT_DOCUMENT_BACKDROP_WIDTH_PARAM = "width";

/**
 * The four vendored `woff2` subsets in `public/fonts`, and the `@font-face` descriptor each one
 * carries.
 *
 * ONE list, driving BOTH the fetch that turns each file into a base64 `data:` URI
 * (`resolveImpactReportDocumentFonts`) and the `@font-face` rules the document declares. Two lists
 * would let the document declare a face it never embedded — which fails silently, as a heading in
 * the platform's default sans.
 *
 * `path` is the app-origin path the browser fetches; it never reaches the serialised document,
 * which carries the bytes instead. `family` MUST match the family names `--font-lato` and
 * `--font-crimson` carry in `styles/impact-report-document-tokens.css`, or the faces are embedded
 * and never used.
 */
export const IMPACT_REPORT_DOCUMENT_FONT_FACES = [
  { key: "latoRegular", path: "/fonts/lato-400.woff2", family: "Lato", weight: 400 },
  { key: "latoBold", path: "/fonts/lato-700.woff2", family: "Lato", weight: 700 },
  {
    key: "crimsonRegular",
    path: "/fonts/crimsontext-400.woff2",
    family: "Crimson Text",
    weight: 400,
  },
  {
    key: "crimsonSemiBold",
    path: "/fonts/crimsontext-600.woff2",
    family: "Crimson Text",
    weight: 600,
  },
] as const;

/**
 * `@font-face` descriptors every vendored face shares; only family, weight and bytes vary.
 *
 * `format` is what the engine matches on, so the `data:` URI's own media type — whatever the
 * server that served the file declared — never decides whether the face loads. `display` is
 * `block` rather than `swap`: a print render has one shot at the layout, and a fallback face
 * swapped in mid-render would set the report's display type in the wrong metrics with nothing
 * left to correct it.
 */
export const IMPACT_REPORT_DOCUMENT_FONT_FACE_DESCRIPTORS = {
  style: "normal",
  display: "block",
  format: "woff2",
} as const;

/**
 * The printed sheets this document puts IN FRONT OF the hero pages: the print-only cover, which
 * `IMPACT_REPORT_DOCUMENT_GEOMETRY_CSS` below gives a sheet of its own (`.irv-pdf-cover`'s
 * `break-after: page`). It replaces the masthead strip as that single sheet, so the count is
 * unchanged. The wire's photo credits number the hero pages from the summary page as page 1, so
 * every credit prints this many sheets later in the PDF than its wire number says.
 *
 * Platform's own fact about a sheet Platform added, never a number read off the payload. It and
 * the cover's `break-after` change TOGETHER: drop the cover and this becomes 0. The back cover
 * prints AFTER the hero pages and shifts no credit.
 */
export const IMPACT_REPORT_DOCUMENT_SHEETS_BEFORE_PAGES = 1;

/**
 * Everything the document stylesheet needs that a standalone document has no other way to get:
 * the print page box, the size and page-break rule of every printed sheet, the ONE token whose
 * value the document deliberately differs on, and the element defaults `index.css` states for the
 * app and this document would otherwise be missing. Emitted after the shared tokens and before the
 * stylesheet.
 *
 * ALL PAGE GEOMETRY LIVES HERE, NOT IN THE STYLESHEET'S PRINT BLOCK. These rules are unlayered and
 * the stylesheet is `@layer components`, so a rule here beats any layered rule whatever its
 * specificity; a sheet height or margin written in the print block would lose to it silently.
 *
 * THE RESET IS IN `@layer base`, AND THAT IS LOAD-BEARING RATHER THAN TIDY. An unlayered `*` rule
 * BEATS every layered one, so a universal `margin: 0; padding: 0` written outside a layer would
 * silently win against `.irv-page`'s own padding and `.irv-doc`'s own margins — the document
 * stylesheet lives in `@layer components`. Layering it below `components`, exactly as `index.css`
 * does, is what makes the reset a floor instead of an override.
 *
 * IT IS DELIBERATELY NOT A SECOND COPY OF ANY `.irv-*` RULE OR DESIGN TOKEN. The rules that name an
 * `.irv-*` selector set sheet geometry and nothing else; every shared token is declared once, in
 * `styles/impact-report-document-tokens.css`. The `:root` block carries only what is TRUE OF THIS
 * DOCUMENT AND NOT THE SCREEN:
 *   - `--app-main-padding: 0px` — on screen this is the app shell's content padding, which the
 *     report's rules cancel with negative offsets. A standalone document has no shell to cancel
 *     (and `index.css`, which defines the token, is not embedded), so zeroing it collapses every
 *     such offset at once.
 *   - `color-scheme: light` — the converter's UA has no app shell to inherit a scheme from.
 */
export const IMPACT_REPORT_DOCUMENT_GEOMETRY_CSS = `@layer base, components;

@page {
  size: ${IMPACT_REPORT_DOCUMENT_PAGE_WIDTH_PX}px ${IMPACT_REPORT_DOCUMENT_PAGE_HEIGHT_PX}px;
  margin: 0;
}

/* box-sizing decides whether this document paginates at all: with the browser default,
   .irv-page's 140px of vertical padding sits OUTSIDE its height and every page box spills a
   sliver onto a sheet of its own. */
@layer base {
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
}

:root {
  --app-main-padding: 0px;
  color-scheme: light;
}

/* Every page is a full-bleed photograph under a scrim; a print engine drops backgrounds unless
   told not to, which would print the report white-on-white. */
html {
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* index.css's own body rule, less the app shell's page colour: this document's ground is white,
   the same --color-white .irv-doc paints itself with. */
body {
  font-family: var(--font-lato);
  font-size: 16px;
  color: var(--color-rodeo-black);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: var(--color-white);
}

/* .irv-doc's on-screen flex gap and top margin each add height between sheets, and a margin
   AFTER a forced break is kept, not truncated, so either pushes a page past its sheet and prints
   a sliver of it on an extra, otherwise blank one. */
.irv-doc {
  display: block;
  gap: 0;
  margin: 0;
}

/* One printed sheet per element, each exactly one page box tall: an explicit height rather than
   100vh, which is the converter's viewport and not necessarily the page box. overflow: hidden
   keeps anything taller than the sheet from spilling onto the next. */
.irv-page,
.irv-pdf-cover,
.irv-pdf-back {
  height: ${IMPACT_REPORT_DOCUMENT_PAGE_HEIGHT_PX}px;
  min-height: 0;
  overflow: hidden;
  break-inside: avoid;
}

.irv-page {
  break-after: page;
}

/* The cover's sheet is the one IMPACT_REPORT_DOCUMENT_SHEETS_BEFORE_PAGES counts. */
.irv-pdf-cover {
  break-after: page;
}

/* Named rather than matched by position: the back cover is a sibling of .irv-doc, so a
   last-sibling selector inside .irv-doc would match the closing page instead. A break after the
   final sheet prints a blank one. */
.irv-pdf-back {
  break-after: auto;
}
`;

/**
 * Where the serialiser reads the document's stylesheets from at runtime.
 *
 * These are COPIES of `src/styles/impact-report-document{,-tokens}.css`, placed in `public/styles`
 * by the `copy:document-css` step in this app's `dev` and `build` scripts. `src/styles` remains
 * the single source — `index.css` `@import`s those same files for the screen — and the copies exist
 * only because Bun's bundler cannot hand a `.css` file back as text: the dev bundler throws a
 * `ReferenceError` on the import and the production build silently drops it.
 */
export const IMPACT_REPORT_DOCUMENT_CSS_SRC = "/styles/impact-report-document.css";
export const IMPACT_REPORT_DOCUMENT_TOKENS_CSS_SRC = "/styles/impact-report-document-tokens.css";

/**
 * Matches the tokens file's top-level Tailwind `@theme { … }` wrapper, which the serialiser
 * rewrites to `:root { … }` so a converter running no Tailwind build still resolves every `var()`.
 */
export const IMPACT_REPORT_DOCUMENT_THEME_AT_RULE = /(^|\n)@theme(\s*\{)/;

/**
 * The `Content-Type` a served stylesheet must carry. Anything else — above all the SPA's
 * `index.html` — means the request missed the file and must not be embedded as CSS.
 */
export const IMPACT_REPORT_DOCUMENT_CSS_CONTENT_TYPE = "text/css";
